import { useState, useEffect, useMemo } from "react";
import { api } from "@/App";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { 
  TrendingUp, 
  ChevronDown, 
  ChevronRight, 
  Info, 
  Calculator,
  Target,
  Sparkles,
  DollarSign,
  Settings2
} from "lucide-react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine
} from "recharts";

const formatCurrency = (amount) => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

const formatCurrencyShort = (amount) => {
  if (amount >= 1000000) {
    return `$${(amount / 1000000).toFixed(1)}M`;
  } else if (amount >= 1000) {
    return `$${(amount / 1000).toFixed(0)}K`;
  }
  return formatCurrency(amount);
};

export default function LongTermPlanning() {
  const [loading, setLoading] = useState(true);
  const [availableYears, setAvailableYears] = useState([2024, 2025, 2026]);
  const [budgetGoalsData, setBudgetGoalsData] = useState({});

  // Control Panel State
  const [baselineYear, setBaselineYear] = useState(new Date().getFullYear());
  const [startingAssets, setStartingAssets] = useState("1000000");
  const [contribution401kPercent, setContribution401kPercent] = useState("8");
  const [annual401kCap, setAnnual401kCap] = useState("23500");
  const [taxWithholdingPercent, setTaxWithholdingPercent] = useState("33");
  const [investmentReturnPercent, setInvestmentReturnPercent] = useState("6");
  const [capitalGainsFeesPercent, setCapitalGainsFeesPercent] = useState("15");
  const [withdrawalRatePercent, setWithdrawalRatePercent] = useState("4");

  // Default Growth Rates
  const [incomeGrowthPercent, setIncomeGrowthPercent] = useState("1");
  const [billsGrowthPercent, setBillsGrowthPercent] = useState("1");
  const [needsGrowthPercent, setNeedsGrowthPercent] = useState("1");
  const [wantsGrowthPercent, setWantsGrowthPercent] = useState("1");

  // Year Overrides (collapsed section)
  const [showYearOverrides, setShowYearOverrides] = useState(false);
  const [yearOverrides, setYearOverrides] = useState({}); // { year: { income: "2", bills: "1.5", ... } }

  // One-Time Items per year
  const [oneTimeItems, setOneTimeItems] = useState({}); // { year: { otherIncome: "", otherIncomeNote: "", otherExpense: "", otherExpenseNote: "" } }

  // Baseline values from Budget Goals
  const [grossIncome, setGrossIncome] = useState(0);
  const [bills, setBills] = useState(0);
  const [needs, setNeeds] = useState(0);
  const [wants, setWants] = useState(0);

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    if (baselineYear) {
      fetchBudgetGoalsForYear(baselineYear);
    }
  }, [baselineYear]);

  const fetchInitialData = async () => {
    try {
      setLoading(true);
      // We'll set available years based on what years have budget goals
      const years = [2024, 2025, 2026];
      setAvailableYears(years);
    } catch (error) {
      toast.error("Failed to load initial data");
    } finally {
      setLoading(false);
    }
  };

  const fetchBudgetGoalsForYear = async (year) => {
    try {
      const [goalsRes, catsRes] = await Promise.all([
        api.get(`/budget-goals?year=${year}`),
        api.get("/categories"),
      ]);

      const goals = goalsRes.data;
      const categories = catsRes.data;

      // Calculate totals by group
      let incomeTotal = 0;
      let billsTotal = 0;
      let needsTotal = 0;
      let wantsTotal = 0;

      goals.forEach((goal) => {
        const cat = categories.find((c) => c.id === goal.category_id);
        if (!cat || goal.period_type === "na") return;

        const annualAmount = goal.annual_amount || 0;
        switch (cat.group) {
          case "Income":
            incomeTotal += annualAmount;
            break;
          case "Bills":
            billsTotal += annualAmount;
            break;
          case "Needs":
            needsTotal += annualAmount;
            break;
          case "Wants":
            wantsTotal += annualAmount;
            break;
        }
      });

      // Income from budget goals is take-home, we need to back-calculate gross
      // For now, let user see and adjust
      // Actually, looking at the spreadsheet, Income in Budget Goals IS take-home
      // We need Gross Income as a separate input, or we can reverse-engineer it

      // Let's set take-home as the income total and let the user input gross separately
      // Actually, from the spec: budget goals = take-home income
      // So we need to add Gross Income as a manual input too

      setGrossIncome(incomeTotal); // This is actually take-home from budget goals
      setBills(billsTotal);
      setNeeds(needsTotal);
      setWants(wantsTotal);

      setBudgetGoalsData({ income: incomeTotal, bills: billsTotal, needs: needsTotal, wants: wantsTotal });

    } catch (error) {
      toast.error("Failed to load budget goals for year");
    }
  };

  // Run projection calculations
  const projection = useMemo(() => {
    const years = [];
    const maxYears = 25;

    // Parse control panel values
    const startAssets = parseFloat(startingAssets) || 0;
    const contrib401kPct = parseFloat(contribution401kPercent) / 100 || 0;
    const cap401k = parseFloat(annual401kCap) || 23500;
    const taxPct = parseFloat(taxWithholdingPercent) / 100 || 0.33;
    const returnPct = parseFloat(investmentReturnPercent) / 100 || 0.06;
    const capGainsPct = parseFloat(capitalGainsFeesPercent) / 100 || 0.15;
    const withdrawalPct = parseFloat(withdrawalRatePercent) / 100 || 0.04;

    const incomeGrowth = parseFloat(incomeGrowthPercent) / 100 || 0.01;
    const billsGrowth = parseFloat(billsGrowthPercent) / 100 || 0.01;
    const needsGrowth = parseFloat(needsGrowthPercent) / 100 || 0.01;
    const wantsGrowth = parseFloat(wantsGrowthPercent) / 100 || 0.01;

    // Starting values (from budget goals, which is take-home)
    // We need to work backwards to get gross income
    // Take-home = Gross - 401k - Taxes
    // But we have take-home from budget goals... 
    // Let's assume user enters gross income manually or we use a simple model

    // For this model: Use the budget goals income as the TAKE-HOME baseline
    // And let the user specify Gross Income separately in the control panel
    // OR: We reverse-calculate. Let's keep it simple:
    // Budget Goals "Income" = Take-Home Income (net of 401k and taxes)

    // From spreadsheet:
    // Take-Home = Gross - 401k - Taxes
    // So if user has Budget Goals Income = 250K take-home
    // We need them to also specify gross income for the 401k/tax calculation

    // Let me add Gross Income as a separate field (we're pulling take-home from goals)
    // Actually, let's just use the income from goals and assume it's take-home

    let currentGross = parseFloat(grossIncome) || 250000; // If budget goals income is take-home, we need gross
    // Hmm, this is tricky. Let's assume for simplicity that user has GROSS in control panel

    // Actually, re-reading the spec: Budget Goals stores take-home income categories (salary after deductions)
    // So we need the user to input Gross Income manually
    // For now, let's back-calculate: if take-home is X and tax rate is 33% and 401k is 8%
    // Gross = TakeHome / (1 - taxRate - 401kRate)

    const takeHomeFromGoals = budgetGoalsData.income || 250000;
    // Gross = TakeHome / (1 - taxPct - contrib401kPct)
    // But 401k has a cap, so this is approximate
    const impliedGross = takeHomeFromGoals / (1 - taxPct - Math.min(contrib401kPct, cap401k / (takeHomeFromGoals / (1 - taxPct - contrib401kPct))));
    
    // Let's simplify: just let user enter gross income directly
    // We'll add a grossIncome state. For now, derive it roughly:
    currentGross = takeHomeFromGoals / (1 - taxPct - contrib401kPct * 0.7); // rough approximation

    let currentBills = budgetGoalsData.bills || 28000;
    let currentNeeds = budgetGoalsData.needs || 49500;
    let currentWants = budgetGoalsData.wants || 125000;

    let assets = startAssets;
    let goalYearReached = null;

    for (let i = 0; i < maxYears; i++) {
      const year = baselineYear + i;

      // Get overrides for this year
      const overrides = yearOverrides[year] || {};
      const yearIncomeGrowth = overrides.income !== undefined ? parseFloat(overrides.income) / 100 : incomeGrowth;
      const yearBillsGrowth = overrides.bills !== undefined ? parseFloat(overrides.bills) / 100 : billsGrowth;
      const yearNeedsGrowth = overrides.needs !== undefined ? parseFloat(overrides.needs) / 100 : needsGrowth;
      const yearWantsGrowth = overrides.wants !== undefined ? parseFloat(overrides.wants) / 100 : wantsGrowth;

      // Apply growth (except first year which uses baseline)
      if (i > 0) {
        currentGross *= (1 + yearIncomeGrowth);
        currentBills *= (1 + yearBillsGrowth);
        currentNeeds *= (1 + yearNeedsGrowth);
        currentWants *= (1 + yearWantsGrowth);
      }

      // Get one-time items for this year
      const oneTime = oneTimeItems[year] || {};
      const otherIncome = parseFloat(oneTime.otherIncome) || 0;
      const otherExpense = parseFloat(oneTime.otherExpense) || 0;

      // Calculate 401k (capped)
      const contribution401k = Math.min(currentGross * contrib401kPct, cap401k);

      // Calculate taxes
      const taxes = currentGross * taxPct;

      // Take-home income
      const takeHome = currentGross - contribution401k - taxes + otherIncome;

      // Expenses
      const totalExpenses = currentBills + currentNeeds + currentWants + otherExpense;
      const lifestyleExpenses = totalExpenses; // For goal calculation

      // Budget savings (what goes into investable assets)
      const budgetSavings = takeHome - totalExpenses + contribution401k; // 401k also goes to investable assets

      // Investment returns (on start-of-year assets)
      const grossReturns = assets * returnPct;
      const netReturns = grossReturns * (1 - capGainsPct);

      // End of year assets
      const endAssets = assets + budgetSavings + netReturns;

      // Implied dividend (sustainable distribution from end-of-year assets)
      const impliedDividend = endAssets * withdrawalPct;

      // Check if goal reached (implied dividend >= lifestyle expenses)
      if (goalYearReached === null && impliedDividend >= lifestyleExpenses) {
        goalYearReached = year;
      }

      years.push({
        year,
        grossIncome: currentGross,
        contribution401k,
        taxes,
        takeHome,
        bills: currentBills,
        needs: currentNeeds,
        wants: currentWants,
        otherIncome,
        otherExpense,
        lifestyleExpenses,
        budgetSavings: takeHome - totalExpenses,
        totalSavings: budgetSavings,
        startAssets: assets,
        grossReturns,
        netReturns,
        endAssets,
        impliedDividend,
        goalReached: impliedDividend >= lifestyleExpenses,
        // For override display
        hasOverrides: Object.keys(overrides).length > 0,
        hasOneTimeItems: otherIncome > 0 || otherExpense > 0,
      });

      assets = endAssets;

      // Stop if goal reached and we've shown a few more years
      if (goalYearReached && year >= goalYearReached + 3) {
        break;
      }
    }

    return { years, goalYear: goalYearReached };
  }, [
    baselineYear, startingAssets, contribution401kPercent, annual401kCap,
    taxWithholdingPercent, investmentReturnPercent, capitalGainsFeesPercent,
    withdrawalRatePercent, incomeGrowthPercent, billsGrowthPercent,
    needsGrowthPercent, wantsGrowthPercent, yearOverrides, oneTimeItems,
    budgetGoalsData
  ]);

  // Calculate savings materiality
  const savingsMateriality = useMemo(() => {
    if (!projection.goalYear) return null;

    // Run a second projection with +$10K annual savings
    const additionalSavings = 10000;
    
    // Simplified: each $X saved pulls goal forward by roughly X / (assets * return)
    // More accurate: run the projection again with reduced wants

    // For now, estimate: additional savings per year compounds
    // This is a rough heuristic
    const yearsToGoal = projection.goalYear - baselineYear;
    const avgReturn = parseFloat(investmentReturnPercent) / 100;
    
    // Future value of additional annual savings over yearsToGoal years
    // FV = PMT * ((1+r)^n - 1) / r
    const fvAdditional = additionalSavings * ((Math.pow(1 + avgReturn, yearsToGoal) - 1) / avgReturn);
    
    // This additional amount at goal year, divided by annual savings need
    // gives approximately how many years it pulls forward
    const withdrawalRate = parseFloat(withdrawalRatePercent) / 100;
    const targetAssets = (projection.years[projection.years.length - 1]?.lifestyleExpenses || 200000) / withdrawalRate;
    
    // Rough estimate: years pulled forward ≈ fvAdditional / (targetAssets * avgReturn)
    const yearsPulledForward = fvAdditional / (targetAssets * avgReturn);

    // Find amount needed to pull forward by 1 year
    const amountFor1Year = additionalSavings / Math.max(yearsPulledForward, 0.1);

    return {
      additionalSavings,
      yearsPulledForward: Math.round(yearsPulledForward * 10) / 10,
      amountFor1Year: Math.round(amountFor1Year / 1000) * 1000,
    };
  }, [projection, baselineYear, investmentReturnPercent, withdrawalRatePercent]);

  // Chart data
  const chartData = useMemo(() => {
    return projection.years.map((y) => ({
      year: y.year,
      assets: y.endAssets,
      goalLine: y.lifestyleExpenses / (parseFloat(withdrawalRatePercent) / 100),
      impliedDividend: y.impliedDividend,
      lifestyleExpenses: y.lifestyleExpenses,
    }));
  }, [projection, withdrawalRatePercent]);

  const updateYearOverride = (year, field, value) => {
    setYearOverrides((prev) => ({
      ...prev,
      [year]: {
        ...(prev[year] || {}),
        [field]: value,
      },
    }));
  };

  const updateOneTimeItem = (year, field, value) => {
    setOneTimeItems((prev) => ({
      ...prev,
      [year]: {
        ...(prev[year] || {}),
        [field]: value,
      },
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <TooltipProvider>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold font-['Outfit'] text-slate-900 flex items-center gap-2">
              <Sparkles className="h-6 w-6 text-amber-500" strokeWidth={1.5} />
              What-If Machine
            </h1>
            <p className="text-slate-500 mt-1">
              Long-term projection: when can investment income cover lifestyle expenses?
            </p>
          </div>
        </div>

        {/* Goal & Materiality Callouts */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Goal Year */}
          <Card className={`border-2 ${projection.goalYear ? 'border-emerald-500 bg-emerald-50' : 'border-amber-500 bg-amber-50'}`}>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className={`p-3 rounded-full ${projection.goalYear ? 'bg-emerald-100' : 'bg-amber-100'}`}>
                  <Target className={`h-6 w-6 ${projection.goalYear ? 'text-emerald-600' : 'text-amber-600'}`} strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-600">Financial Independence Target</p>
                  {projection.goalYear ? (
                    <>
                      <p className="text-3xl font-bold text-emerald-700">{projection.goalYear}</p>
                      <p className="text-sm text-slate-500">{projection.goalYear - baselineYear} years from now</p>
                    </>
                  ) : (
                    <>
                      <p className="text-2xl font-bold text-amber-700">25+ years</p>
                      <p className="text-sm text-slate-500">Goal not reached within projection window</p>
                    </>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Savings Materiality */}
          <Card className="border-slate-200">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-3 rounded-full bg-sky-100">
                  <Calculator className="h-6 w-6 text-sky-600" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-600">Savings Materiality</p>
                  {savingsMateriality ? (
                    <>
                      <p className="text-xl font-bold text-slate-800">
                        +{formatCurrency(savingsMateriality.amountFor1Year)}/yr → 1 year earlier
                      </p>
                      <p className="text-sm text-slate-500">
                        +$10K/yr savings → {savingsMateriality.yearsPulledForward} years earlier
                      </p>
                    </>
                  ) : (
                    <p className="text-sm text-slate-500">Set goal to calculate</p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Control Panel */}
          <Card className="lg:col-span-1 border-slate-200">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg font-['Outfit'] flex items-center gap-2">
                <Settings2 className="h-5 w-5 text-slate-400" strokeWidth={1.5} />
                Control Panel
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Baseline Year */}
              <div className="space-y-2">
                <Label className="text-xs font-medium text-slate-500">Baseline Year (from Budget Goals)</Label>
                <Select value={String(baselineYear)} onValueChange={(v) => setBaselineYear(Number(v))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {availableYears.map((y) => (
                      <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {budgetGoalsData.income > 0 && (
                  <p className="text-xs text-slate-500">
                    Loaded: Income {formatCurrency(budgetGoalsData.income)}, Bills {formatCurrency(budgetGoalsData.bills)}, Needs {formatCurrency(budgetGoalsData.needs)}, Wants {formatCurrency(budgetGoalsData.wants)}
                  </p>
                )}
              </div>

              {/* Starting Assets */}
              <div className="space-y-2">
                <Label className="text-xs font-medium text-slate-500">Starting Investable Assets</Label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    type="number"
                    value={startingAssets}
                    onChange={(e) => setStartingAssets(e.target.value)}
                    className="pl-9"
                    placeholder="1000000"
                  />
                </div>
              </div>

              {/* 401k Section */}
              <div className="space-y-3 p-3 bg-slate-50 rounded-lg">
                <Label className="text-xs font-medium text-slate-500">401k Settings</Label>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs text-slate-400">Contribution %</Label>
                    <div className="relative">
                      <Input
                        type="number"
                        value={contribution401kPercent}
                        onChange={(e) => setContribution401kPercent(e.target.value)}
                        className="pr-7 text-sm"
                        step="0.5"
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">%</span>
                    </div>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-slate-400">Annual Cap</Label>
                    <div className="relative">
                      <DollarSign className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-slate-400" />
                      <Input
                        type="number"
                        value={annual401kCap}
                        onChange={(e) => setAnnual401kCap(e.target.value)}
                        className="pl-6 text-sm"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Tax Rate */}
              <div className="space-y-2">
                <Label className="text-xs font-medium text-slate-500">Tax / Withholding Rate</Label>
                <div className="relative">
                  <Input
                    type="number"
                    value={taxWithholdingPercent}
                    onChange={(e) => setTaxWithholdingPercent(e.target.value)}
                    className="pr-7"
                    step="1"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">%</span>
                </div>
              </div>

              {/* Investment Returns */}
              <div className="space-y-3 p-3 bg-slate-50 rounded-lg">
                <Label className="text-xs font-medium text-slate-500">Investment Assumptions</Label>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs text-slate-400">Return Rate</Label>
                    <div className="relative">
                      <Input
                        type="number"
                        value={investmentReturnPercent}
                        onChange={(e) => setInvestmentReturnPercent(e.target.value)}
                        className="pr-7 text-sm"
                        step="0.5"
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">%</span>
                    </div>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-slate-400">Cap Gains/Fees</Label>
                    <div className="relative">
                      <Input
                        type="number"
                        value={capitalGainsFeesPercent}
                        onChange={(e) => setCapitalGainsFeesPercent(e.target.value)}
                        className="pr-7 text-sm"
                        step="1"
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">%</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Withdrawal Rate */}
              <div className="space-y-2">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Label className="text-xs font-medium text-slate-500 flex items-center gap-1 cursor-help">
                      Sustainable Withdrawal Rate
                      <Info className="h-3 w-3 opacity-50" />
                    </Label>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-xs">
                    <p className="text-xs">The percentage of assets that can be sustainably withdrawn annually (commonly 4% "rule of thumb")</p>
                  </TooltipContent>
                </Tooltip>
                <div className="relative">
                  <Input
                    type="number"
                    value={withdrawalRatePercent}
                    onChange={(e) => setWithdrawalRatePercent(e.target.value)}
                    className="pr-7"
                    step="0.5"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">%</span>
                </div>
              </div>

              {/* Default Growth Rates */}
              <div className="space-y-3 p-3 bg-slate-50 rounded-lg">
                <Label className="text-xs font-medium text-slate-500">Default Annual Growth Rates</Label>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs text-emerald-600">Income</Label>
                    <div className="relative">
                      <Input
                        type="number"
                        value={incomeGrowthPercent}
                        onChange={(e) => setIncomeGrowthPercent(e.target.value)}
                        className="pr-7 text-sm"
                        step="0.5"
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">%</span>
                    </div>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-rose-600">Bills</Label>
                    <div className="relative">
                      <Input
                        type="number"
                        value={billsGrowthPercent}
                        onChange={(e) => setBillsGrowthPercent(e.target.value)}
                        className="pr-7 text-sm"
                        step="0.5"
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">%</span>
                    </div>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-amber-600">Needs</Label>
                    <div className="relative">
                      <Input
                        type="number"
                        value={needsGrowthPercent}
                        onChange={(e) => setNeedsGrowthPercent(e.target.value)}
                        className="pr-7 text-sm"
                        step="0.5"
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">%</span>
                    </div>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-purple-600">Wants</Label>
                    <div className="relative">
                      <Input
                        type="number"
                        value={wantsGrowthPercent}
                        onChange={(e) => setWantsGrowthPercent(e.target.value)}
                        className="pr-7 text-sm"
                        step="0.5"
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">%</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Year Overrides - Collapsed */}
              <Collapsible open={showYearOverrides} onOpenChange={setShowYearOverrides}>
                <CollapsibleTrigger asChild>
                  <Button variant="ghost" className="w-full justify-between text-xs text-slate-500 hover:text-slate-700">
                    <span>Year-Specific Overrides</span>
                    {showYearOverrides ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                  </Button>
                </CollapsibleTrigger>
                <CollapsibleContent className="pt-2">
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {projection.years.slice(0, 10).map((y) => (
                      <div key={y.year} className="flex items-center gap-2 text-xs">
                        <span className="w-12 font-medium">{y.year}</span>
                        <Input
                          type="number"
                          placeholder="Inc%"
                          value={yearOverrides[y.year]?.income || ""}
                          onChange={(e) => updateYearOverride(y.year, "income", e.target.value)}
                          className="w-16 h-7 text-xs"
                        />
                        <Input
                          type="number"
                          placeholder="Bills%"
                          value={yearOverrides[y.year]?.bills || ""}
                          onChange={(e) => updateYearOverride(y.year, "bills", e.target.value)}
                          className="w-16 h-7 text-xs"
                        />
                        <Input
                          type="number"
                          placeholder="Needs%"
                          value={yearOverrides[y.year]?.needs || ""}
                          onChange={(e) => updateYearOverride(y.year, "needs", e.target.value)}
                          className="w-16 h-7 text-xs"
                        />
                        <Input
                          type="number"
                          placeholder="Wants%"
                          value={yearOverrides[y.year]?.wants || ""}
                          onChange={(e) => updateYearOverride(y.year, "wants", e.target.value)}
                          className="w-16 h-7 text-xs"
                        />
                      </div>
                    ))}
                  </div>
                </CollapsibleContent>
              </Collapsible>
            </CardContent>
          </Card>

          {/* Chart & Projection Table */}
          <div className="lg:col-span-2 space-y-6">
            {/* Chart */}
            <Card className="border-slate-200">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg font-['Outfit'] flex items-center gap-2">
                  <TrendingUp className="h-5 w-5 text-slate-400" strokeWidth={1.5} />
                  Projection Chart
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="year" tick={{ fontSize: 12 }} stroke="#94a3b8" />
                      <YAxis 
                        tickFormatter={(v) => formatCurrencyShort(v)} 
                        tick={{ fontSize: 12 }} 
                        stroke="#94a3b8"
                      />
                      <RechartsTooltip
                        formatter={(value, name) => [formatCurrency(value), name]}
                        labelFormatter={(label) => `Year ${label}`}
                        contentStyle={{ fontSize: 12 }}
                      />
                      <Legend wrapperStyle={{ fontSize: 12 }} />
                      <Line
                        type="monotone"
                        dataKey="assets"
                        name="Investable Assets"
                        stroke="#3b82f6"
                        strokeWidth={2}
                        dot={false}
                      />
                      <Line
                        type="monotone"
                        dataKey="goalLine"
                        name="Assets Needed (Goal)"
                        stroke="#10b981"
                        strokeWidth={2}
                        strokeDasharray="5 5"
                        dot={false}
                      />
                      {projection.goalYear && (
                        <ReferenceLine
                          x={projection.goalYear}
                          stroke="#10b981"
                          strokeWidth={2}
                          label={{ value: `FI: ${projection.goalYear}`, position: "top", fontSize: 12 }}
                        />
                      )}
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Projection Table */}
            <Card className="border-slate-200">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg font-['Outfit']">Projection Details</CardTitle>
                <CardDescription>Year-by-year breakdown with one-time items</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-200 text-xs text-slate-500">
                        <th className="text-left py-2 px-2 font-medium">Year</th>
                        <th className="text-right py-2 px-2 font-medium">Take-Home</th>
                        <th className="text-right py-2 px-2 font-medium">Expenses</th>
                        <th className="text-right py-2 px-2 font-medium">Savings</th>
                        <th className="text-right py-2 px-2 font-medium">Assets</th>
                        <th className="text-right py-2 px-2 font-medium">Dividend</th>
                        <th className="text-center py-2 px-2 font-medium">Other Income</th>
                        <th className="text-center py-2 px-2 font-medium">Other Expense</th>
                      </tr>
                    </thead>
                    <tbody>
                      {projection.years.map((y, idx) => (
                        <tr 
                          key={y.year} 
                          className={`border-b border-slate-100 ${y.goalReached ? 'bg-emerald-50' : ''} ${y.hasOverrides ? 'bg-amber-50/50' : ''}`}
                        >
                          <td className="py-2 px-2 font-medium">
                            {y.year}
                            {y.goalReached && idx === projection.years.findIndex(yr => yr.goalReached) && (
                              <Badge className="ml-2 bg-emerald-500 text-xs">FI</Badge>
                            )}
                          </td>
                          <td className="text-right py-2 px-2 font-mono text-emerald-600">{formatCurrencyShort(y.takeHome)}</td>
                          <td className="text-right py-2 px-2 font-mono text-rose-600">{formatCurrencyShort(y.lifestyleExpenses)}</td>
                          <td className="text-right py-2 px-2 font-mono text-sky-600">{formatCurrencyShort(y.totalSavings)}</td>
                          <td className="text-right py-2 px-2 font-mono font-semibold">{formatCurrencyShort(y.endAssets)}</td>
                          <td className={`text-right py-2 px-2 font-mono ${y.goalReached ? 'text-emerald-600 font-semibold' : 'text-slate-500'}`}>
                            {formatCurrencyShort(y.impliedDividend)}
                          </td>
                          <td className="text-center py-2 px-1">
                            <Input
                              type="number"
                              placeholder="0"
                              value={oneTimeItems[y.year]?.otherIncome || ""}
                              onChange={(e) => updateOneTimeItem(y.year, "otherIncome", e.target.value)}
                              className="w-20 h-7 text-xs mx-auto"
                            />
                          </td>
                          <td className="text-center py-2 px-1">
                            <Input
                              type="number"
                              placeholder="0"
                              value={oneTimeItems[y.year]?.otherExpense || ""}
                              onChange={(e) => updateOneTimeItem(y.year, "otherExpense", e.target.value)}
                              className="w-20 h-7 text-xs mx-auto"
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </TooltipProvider>
  );
}
